import { Box, Flex, TableContainer, Text, useToast } from '@chakra-ui/react'
import { HeadingPages } from '../../../components/HeadingPages'
import React from 'react'
import * as T from '../../../components/Table'
import { api } from '../../../services/api'
import { useInfiniteQuery, useQueryClient } from 'react-query'
import { SearchForm } from './SearchForm'
import { ListContacts } from './ListContacts'
import { user } from '../../../helpers/user'
import { OrderBy } from './OrderBy'
import { useInView } from 'react-intersection-observer'
import { MaisClientes } from '../Clientes/MaisClientes'

async function getClientesSemVendedor({ page, limit, search, codigo_vendedor, ...rest }) {
  const response = await api.get('/orcamento-pedido/listar-sem-vendedores', {
    params: {
      page,
      limit,
      busca: search?.busca,
      codigo_vendedor,
      ...rest,
    },
  })

  return response.data
}

export function ClientesSemVendedor() {
  const [errorMessage, setErrorMessage] = React.useState(null)
  const { ref, inView } = useInView({
    delay: 0,
    threshold: 0,
  })

  const toast = useToast({
    position: 'top',
    duration: 5000,
    isClosable: true,
  })

  const [busca, setBusca] = React.useState(null)

  const queryClient = useQueryClient()

  const { data, refetch, isLoading, isRefetching, fetchNextPage, isError } = useInfiniteQuery({
    queryKey: ['clientes-sem-vendedores', busca],
    queryFn: ({ pageParam = 0 }) =>
      getClientesSemVendedor({ ...busca, page: pageParam, limit: 200, search: busca, codigo_vendedor: user.codigo_vendedor }),
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = lastPage.current_page + 1

      return nextPage <= lastPage.last_page ? nextPage : undefined
    },
    refetchOnWindowFocus: false,
    enabled: true,
    keepPreviousData: true,
    retry: 0,

    onError: (error) => {
      toast({
        title: 'Erro ao buscar clientes',
        description: error.response.data.erros.join(', '),
        status: 'error',
      })

      setErrorMessage(error.response.data.erros.join(', '))
    },
  })

  async function onSubmit(data) {
    queryClient.removeQueries('clientes-sem-vendedores')

    setBusca(data)
    refetch()
  }

  function orderBy({ ordem, ordenacao }) {
    queryClient.removeQueries('clientes-sem-vendedores')

    setBusca({ ...busca, ordem, ordenacao })
    refetch()
  }

  React.useEffect(() => {
    if (inView && !busca) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, busca])

  const totalItens = data?.pages[0]?.to

  return (
    <Box>
      <HeadingPages name={data?.pages[0]?.total > 0 ? `Clientes sem Vendedor - Total: ${totalItens}` : 'Clientes sem Vendedor'} />

      <Box
        maxW={{
          base: '1366px',
          md: '1366px',
          lg: '1920px',
        }}
        w="100%"
        h="100%"
        p="2"
        mx="auto"
        display="flex"
        flexDirection="column"
        gap={4}
      >
        <SearchForm onSearch={onSubmit} isLoading={isLoading || isRefetching} />

        {/* <AddCliente /> */}

        <TableContainer>
          <T.Table borderCollapse="collapse">
            <T.Thead>
              <T.Tr py={1}>
                <T.Th py={1}>
                  <Flex alignItems="center" justifyContent="center" gap={2}>
                    Data <br /> Cadastro
                    <OrderBy orderBy={orderBy} name="data_cadastro" />
                  </Flex>
                </T.Th>
                <T.Th py={1}>
                  <Flex alignItems="center" justifyContent="center" gap={2}>
                    Vendedor
                    <OrderBy orderBy={orderBy} name="nome_vendedor" />
                  </Flex>
                </T.Th>
                <T.Th py={1}>
                  <Flex alignItems="center" justifyContent="center" gap={2}>
                    Cliente
                    <OrderBy orderBy={orderBy} name="codigo_parceiro" />
                  </Flex>
                </T.Th>
                <T.Th py={1}>Cnpj/Cpf</T.Th>
                <T.Th py={1}>
                  <Flex alignItems="center" justifyContent="center" gap={2}>
                    Orçamentos
                    <OrderBy orderBy={orderBy} name="quantidade_orcamentos" />
                  </Flex>
                </T.Th>
                <T.Th py={1} w="60px">
                  <Flex alignItems="center" justifyContent="center" gap={2}>
                    Pedidos
                    <OrderBy orderBy={orderBy} name="quantidade_pedidos" />
                  </Flex>
                </T.Th>
                <T.Th py={1}>
                  <Flex alignItems="center" justifyContent="center" gap={2}>
                    Valor
                    <OrderBy orderBy={orderBy} name="valor_orcamento" />
                  </Flex>
                </T.Th>
                <T.Th py={1}>
                  <Flex alignItems="center" justifyContent="center" gap={2}>
                    Origem
                    <OrderBy orderBy={orderBy} name="origem_orcamento_desc" />
                  </Flex>
                </T.Th>
                <T.Th py={1}>Cidade</T.Th>
                <T.Th py={1} w="60px">
                  Estado
                </T.Th>
                <T.Th py={1} w="60px">
                  <Flex alignItems="center" justifyContent="center" gap={2}>
                    Cliente <br /> Especial
                    <OrderBy orderBy={orderBy} name="cliente_especial" />
                  </Flex>
                </T.Th>
                <T.Th py={1}></T.Th>
                <T.Th py={1}></T.Th>
              </T.Tr>
            </T.Thead>
            <T.Tbody>
              {isError && (
                <T.Tr>
                  <T.Td colSpan={12} textAlign="center">
                    <Text color="red.500" textTransform="uppercase" fontSize="sm" fontWeight="bold">
                      {errorMessage}
                    </Text>
                  </T.Td>
                </T.Tr>
              )}
              <ListContacts data={data} />
            </T.Tbody>
          </T.Table>
        </TableContainer>

        <MaisClientes
          ref={ref}
          fetchNextPage={fetchNextPage}
          hasNextPage={data?.pages[0]?.last_page > data?.pages[0]?.current_page}
          isFetchingNextPage={isRefetching}
        />
      </Box>
    </Box>
  )
}
